/* Native Shopify forms stay untouched. Carousels enhance scrollable lists. */
(() => {
  const instances = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  function initialize(root) {
    root.querySelectorAll('[data-bg-carousel]').forEach((carousel) => {
      if (instances.has(carousel)) return;
      const categories = carousel.dataset.bgCarousel === 'categories';
      const row = carousel.querySelector('ul');
      const originals = [...row.children];
      if (originals.length < 2) return;
      const previous = carousel.querySelector('[data-bg-prev]');
      const next = carousel.querySelector('[data-bg-next]');
      const pause = carousel.querySelector('[data-bg-pause]');
      const controller = new AbortController();
      const on = (target, name, handler, options = {}) => target.addEventListener(name, handler, {...options, signal: controller.signal});
      const enabled = carousel.dataset.bgAutoplay !== 'false' && !window.Shopify?.designMode;
      const interval = Number(carousel.dataset.bgInterval) || 7000;
      const speed = Number(carousel.dataset.bgSpeed) || 12;
      let clones = [], cycle = 0, hover = false, focused = false, stopped = false;
      let visible = true, dragging = null, touching = false, suppressClick = false, resumeAt = 0;
      let frame = 0, last = 0, elapsed = 0, animation = null, position = 0;
      const step = () => row.children[1].offsetLeft - row.children[0].offsetLeft;
      const overflow = () => row.scrollWidth > row.clientWidth + 2;
      const busy = () => hover || focused || stopped || dragging || touching || document.hidden || performance.now() < resumeAt;
      const updatePause = () => {
        pause.hidden = !enabled || motion.matches || !overflow();
        pause.setAttribute('aria-label', (stopped ? 'Resume' : 'Pause') + (categories ? ' categories' : ' products'));
        pause.querySelector('span').textContent = stopped ? '▶' : 'Ⅱ';
      };
      function measure() {
        clones.forEach((clone) => clone.remove());
        clones = [];
        if (categories && enabled && !motion.matches) {
          clones = originals.map((item) => {
            const clone = item.cloneNode(true);
            clone.dataset.bgClone = '';
            clone.setAttribute('aria-hidden', 'true');
            [clone, ...clone.querySelectorAll('*')].forEach((node) => {
              node.removeAttribute('id');
              [...node.attributes].filter((a) => a.name.startsWith('data-shopify') || a.name === 'data-qa-block').forEach((a) => node.removeAttribute(a.name));
            });
            clone.querySelectorAll('a,button,[tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
            row.append(clone);
            return clone;
          });
          cycle = clones[0].offsetLeft - originals[0].offsetLeft;
        } else cycle = 0;
        carousel.classList.toggle('bg-carousel-running', enabled && !motion.matches);
        if (previous) previous.hidden = next.hidden = !overflow();
        position = row.scrollLeft;
        animation = null;
        elapsed = 0;
        updatePause();
      }
      // Recycle actual product nodes: no duplicate forms, IDs or cart handlers.
      function normalizeProducts() {
        if (categories || row.contains(document.activeElement) || dragging) return;
        const size = step();
        let count = 0;
        while (row.scrollLeft >= size - 1 && count++ < originals.length) {
          const offset = row.scrollLeft - size;
          row.append(row.firstElementChild);
          row.scrollLeft = Math.max(0, offset);
        }
      }
      function advance(direction) {
        if (!overflow() || animation) return;
        if (direction < 0 && row.scrollLeft < step()) {
          const offset = row.scrollLeft;
          row.prepend(row.lastElementChild);
          row.scrollLeft = offset + step();
        }
        const start = row.scrollLeft;
        const end = start + direction * step();
        if (motion.matches) { row.scrollLeft = end; normalizeProducts(); }
        else animation = {start, end, at: performance.now()};
        elapsed = 0;
      }
      function tick(now) {
        const delta = Math.min(now - (last || now), 50);
        last = now;
        if (animation) {
          const progress = Math.min((now - animation.at) / 850, 1);
          const ease = progress * progress * (3 - 2 * progress);
          row.scrollLeft = animation.start + (animation.end - animation.start) * ease;
          if (progress === 1) { animation = null; normalizeProducts(); }
        } else if (enabled && !motion.matches && !busy() && overflow()) {
          if (categories && cycle) {
            position += speed * delta / 1000;
            if (position >= cycle) position -= cycle;
            row.scrollLeft = position;
          } else {
            normalizeProducts();
            elapsed += delta;
            if (elapsed >= interval) advance(1);
          }
        } else {
          position = row.scrollLeft;
          elapsed = 0;
        }
        if (visible && !document.hidden) frame = requestAnimationFrame(tick);
        else frame = 0;
      }
      function wake() { last = 0; if (!frame && visible && !document.hidden) frame = requestAnimationFrame(tick); }
      const interact = () => { animation = null; elapsed = 0; position = row.scrollLeft; resumeAt = performance.now() + 3000; };
      on(carousel, 'mouseenter', () => { hover = true; animation = null; });
      on(carousel, 'mouseleave', () => { hover = false; });
      on(carousel, 'focusin', () => { focused = true; animation = null; });
      on(carousel, 'focusout', () => queueMicrotask(() => { focused = carousel.contains(document.activeElement); }));
      on(pause, 'click', () => { stopped = !stopped; animation = null; updatePause(); });
      if (previous) {
        on(previous, 'click', () => { interact(); advance(-1); });
        on(next, 'click', () => { interact(); advance(1); });
      }
      on(row, 'wheel', interact, {passive: true});
      on(row, 'keydown', (event) => {
        if (event.target !== row || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); interact();
        if (categories) row.scrollBy({left: step() * (event.key === 'ArrowLeft' ? -1 : 1), behavior: motion.matches ? 'instant' : 'smooth'});
        else advance(event.key === 'ArrowLeft' ? -1 : 1);
      });
      row.tabIndex = 0;
      row.setAttribute('aria-label', categories ? 'Shop categories, scroll for more' : 'Products, scroll for more');
      on(row, 'dragstart', (event) => event.preventDefault());
      on(row, 'pointerdown', (event) => {
        interact();
        touching = event.pointerType !== 'mouse';
        suppressClick = false;
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,select,textarea')) return;
        dragging = {x: event.clientX, scroll: row.scrollLeft, moved: false, id: event.pointerId};
        // Duplicate links stay out of the focus order, while retaining normal link clicks.
        if (event.target.closest('[data-bg-clone]')) event.preventDefault();
      });
      on(row, 'pointermove', (event) => {
        if (!dragging) return;
        const distance = event.clientX - dragging.x;
        if (Math.abs(distance) > 6) {
          dragging.moved = true;
          row.setPointerCapture(dragging.id);
          row.classList.add('bg-dragging');
          row.scrollLeft = dragging.scroll - distance;
          event.preventDefault();
        }
      });
      on(window, 'pointerup', () => {
        if (dragging) suppressClick = dragging.moved;
        dragging = null; touching = false; row.classList.remove('bg-dragging'); interact();
      });
      on(row, 'pointercancel', () => { dragging = null; touching = false; row.classList.remove('bg-dragging'); interact(); });
      on(row, 'click', (event) => {
        if (suppressClick) { event.preventDefault(); event.stopPropagation(); suppressClick = false; }
      }, {capture: true});
      on(row, 'scroll', () => { if (!animation && (busy() || !enabled)) position = row.scrollLeft; }, {passive: true});
      on(document, 'visibilitychange', wake);
      on(motion, 'change', measure);
      const resize = new ResizeObserver(measure);
      resize.observe(row);
      const intersection = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; wake(); });
      intersection.observe(carousel);
      on(document, 'shopify:block:select', (event) => {
        if (carousel.contains(event.target)) { stopped = true; updatePause(); }
      });
      measure(); wake();
      instances.set(carousel, () => {
        cancelAnimationFrame(frame); controller.abort(); resize.disconnect(); intersection.disconnect();
        clones.forEach((clone) => clone.remove());
      });
    });
  }
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((dispose, carousel) => { if (event.target.contains(carousel)) { dispose(); instances.delete(carousel); } });
  });
})();
